// based off of `Textarea.tsx` and this codepen: https://codepen.io/vsync/pen/frudD
import React, { useCallback, useEffect, useState, useRef } from 'react';

interface GrowingTextAreaProps extends React.HTMLProps<HTMLTextAreaElement> {
  canUseGrammarCheck?: boolean;
  shouldFocus?: boolean;
  containerStyle?: React.CSSProperties;
  initialRows?: number;
  maxSizePx?: number;
  disabled?: boolean;
}

const GrowingTextArea: React.FC<GrowingTextAreaProps> = ({
  canUseGrammarCheck,
  shouldFocus,
  containerStyle,
  initialRows,
  ...textareaProps
}) => {
  const [scrollHeight, setScrollHeight] = useState<number | undefined>();
  const maxSizePx = textareaProps.maxSizePx === undefined ? 200 : textareaProps.maxSizePx;
  const minSizePx = 30;
  const editor = useRef<HTMLTextAreaElement>(null);
  const [Sapling, setSapling] = useState<any>(null);

  // Dynamic Import of Sapling
  useEffect(() => {
    import('@scale/frontend-lib/sapling-module')
      .then(mod => {
        setSapling(mod.default);
        return;
      })
      .catch(_ => {
        setSapling(null);
      });
  }, []);

  const handleKeyDown = useCallback((e: any) => {
    e.stopPropagation();
  }, []);

  const handleKeyUp = useCallback((e: any) => {
    e.stopPropagation();
  }, []);

  const getStyle = (): React.CSSProperties => {
    // can't use ternary bc the linter screams about ref.current possibly being null
    // and then gets angry when I do the ! thing
    return scrollHeight
      ? {
          height: Math.max(minSizePx, Math.min(scrollHeight, maxSizePx)) + 'px',
        }
      : {};
  };

  useEffect(() => {
    // Input field in TextField component.
    if (canUseGrammarCheck && editor.current && Sapling) {
      editor.current.setAttribute('contenteditable', 'true');
      editor.current.setAttribute('data-gramm', 'false');
      editor.current.setAttribute('sapling-ignore', 'true');
      Sapling.observe(editor.current);
    }
  }, [canUseGrammarCheck, editor, Sapling]);

  useEffect(() => {
    editor.current && setScrollHeight(editor.current.scrollHeight);
  }, [editor.current?.scrollHeight]);

  return (
    <div className="container" style={containerStyle}>
      <textarea
        className="textarea"
        {...textareaProps}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        ref={editor}
        style={getStyle()}
        rows={1}
      />
      <style jsx>{`
        .container {
          position: relative;
          height: 100%;
          width: 100%;
        }

        textarea {
          background-color: var(--color-RemoGray01);
          padding: 7px 12px;
          box-sizing: border-box;
          border: 0;
          transition: var(--form-transition);
          width: 100%;
          min-height: 100%;
          resize: none;
          overflow: auto;

          border-radius: 8px;

          font-family: var(--font-family);
          font-size: 14px;
        }

        textarea ::placeholder {
          font-weight: normal;
          color: rgba(0, 0, 0, 0.5);
        }

        textarea:focus {
          background-color: var(--bg-form-focus);
        }

        .container.has-icon input {
          text-indent: 20px;
        }
      `}</style>
    </div>
  );
};

export default GrowingTextArea;
